import { BUTTON_BLOCK_TYPE, IBlockDefinition, IBlockProps } from '@fox/blocks';
import { customAlphabet } from 'nanoid';
import { darken, getLuminance, lighten, readableColor } from 'polished';
import { useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { getAttributeValues, transformLegacyProps } from '../../../utils';
import { ButtonBlockProps } from './contracts';

const nanoStyle = customAlphabet('26T198340PX75pxJACKVERYMINDBUSHWOLF_GQZbfghjklqvwyzrict');
export function ButtonBlock({ props, type, designMode }: IBlockProps<ButtonBlockProps> & { designMode?: boolean }) {
	const [isHovered, setIsHovered] = useState<boolean>(false);

	const presetUrl = props.presetUrl;
	const id = useRef('btn' + nanoStyle());

	// Check if transformLegacyProps is still needed - likely redundant due to onload layout helper fns
	const {
		attributes: { padding, margin, horizontalAlign = 'center' }
	} = transformLegacyProps(type, props);

	const { top: paddingTop, bottom: paddingBottom, right: paddingRight, left: paddingLeft } = getAttributeValues(padding);
	const { top: marginTop, bottom: marginBottom, right: marginRight, left: marginLeft } = getAttributeValues(margin);

	const data = props ?? {
		padding: 5,
		borderRadius: 5
	};
	const theme = useTheme();
	const draftJsText = data?.text?.blocks?.[0]?.text;

	// todo get primary out of the form style config
	const background = data?.style?.backgroundColor ?? data?.backgroundColor ?? theme.colors.primary;
	const hoverBackground =
		(data?.style?.hoverBackgroundColour ?? getLuminance(background) > 0.5) ? darken(0.3, background) : lighten(0.3, background);
	const color = data?.style?.color ?? readableColor(background, theme?.colors.white, theme.colors.font);

	const styleObj = {
		...data.style,
		display: 'inline-block',
		paddingTop,
		paddingLeft,
		paddingBottom,
		paddingRight,
		marginLeft,
		marginTop,
		marginRight,
		marginBottom,
		color,
		borderRadius: data?.borderRadius,
		background: isHovered ? hoverBackground : background,
		borderColor: data?.style?.borderColor,
		borderWidth: data?.style?.borderSize,
		borderStyle: data?.style?.borderSize ? 'solid' : undefined
	};

	return (
		<div style={{ textAlign: horizontalAlign }}>
			<style
				dangerouslySetInnerHTML={{
					__html: `
				.${id.current} {
					color: ${color} !important;
					text-decoration: none;
				}`
				}}
			/>
			{designMode ? (
				<span className={`${id.current} canvas-view-button`} style={styleObj}>
					{data.label ?? draftJsText ?? ''}
				</span>
			) : (
				<a
					className={id.current}
					style={styleObj}
					target={data?.target ?? '_self'}
					href={presetUrl ?? data?.url ?? '#'}
					onMouseEnter={() => setIsHovered(true)}
					onMouseLeave={() => setIsHovered(false)}
				>
					{data.label ?? draftJsText ?? ''}
				</a>
			)}
		</div>
	);
}

export const ButtonBlockRuntime: IBlockDefinition<ButtonBlockProps> = {
	type: BUTTON_BLOCK_TYPE,
	Component: ButtonBlock
};
